// Globális dátum+idő választó: a globális `DatePicker` (dátum) + a globális `TimePicker` (idő) kompozíciója —
// a VÁLTOZATLAN `YYYY-MM-DDTHH:mm` (HELYI idő) szerződéssel, drop-in a natív `<input type="datetime-local">`
// helyére. Mindkét fél a rendszer-szintű komponensből jön, tehát NINCS duplikált naptár/popover kód, és nincs
// natív widget sem. A tz-konverziót (UTC↔helyi) a hívó végzi a submit/betöltés határán — a mező a helyi
// stringet tartja.
//
// az idő-fele KORÁBBAN natív `type="time"` volt („idő-választás nem dátum → a natív elfogadott").
// Ez volt az egyetlen natív form-widget a rendszerben, és a megjelenése böngésző-specifikus → elütött a mellette
// álló, stylizált dátum-gombtól. A `TimePicker` a `DatePicker` markupját tükrözi, így a kettő EGY mezőnek látszik.

import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';

export interface DateTimePickerProps {
  /** `YYYY-MM-DDTHH:mm` vagy üres string. */
  value: string;
  onChange: (value: string) => void;
  /** A11y-név (pl. „Kezdés") — placeholder nélkül a látható üres-szöveg is. */
  label?: string;
  /** A DÁTUM-fél látható üres-szövege (formátum-hint), ha a mezőnek van külön címkéje. */
  placeholder?: string | undefined;
  /** Az IDŐ-fél látható üres-szövege (formátum-hint). */
  timePlaceholder?: string | undefined;
  /** UI-nyelv a megjelenített dátum-formátumhoz; alap `hu`. */
  language?: string;
  /** Zárolt mező — a dátum-gomb és az idő-mező is tiltott. */
  disabled?: boolean;
  /** Az idő-rács ABLAKA (`HH:mm`) — a domén saját nyitva-tartása (pl. megbeszélés: 06:00–22:00). */
  minTime?: string | undefined;
  maxTime?: string | undefined;
}

/** `YYYY-MM-DDTHH:mm` → { date: 'YYYY-MM-DD', time: 'HH:mm' } (üres tagok, ha nincs). */
function splitValue(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const [date = '', time = ''] = value.split('T');
  return { date, time: time.slice(0, 5) };
}

export function DateTimePicker({
  value,
  onChange,
  label,
  placeholder,
  timePlaceholder,
  language = 'hu',
  disabled = false,
  minTime,
  maxTime,
}: DateTimePickerProps): JSX.Element {
  const { t } = useTranslation();
  const { date, time } = splitValue(value);

  // A dátum és idő külön változik; összevonáskor üres idő → '00:00' (a datetime-local „kitöltött" szemantikáját
  // tükrözve). Dátum törlése (DatePicker „×") → az egész érték ürül. Idő csak akkor állítható, ha már van dátum
  // (az idő-mező különben tiltott) → sosem keletkezik dátum nélküli, érvénytelen `Thh:mm` érték.
  const emit = (nextDate: string, nextTime: string): void => {
    if (!nextDate) {
      onChange('');
      return;
    }
    onChange(`${nextDate}T${nextTime || '00:00'}`);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <DatePicker
          value={date}
          onChange={(d) => emit(d, time)}
          label={label}
          placeholder={placeholder}
          language={language}
          disabled={disabled}
        />
      </div>
      <div className="w-28 shrink-0">
        <TimePicker
          value={time}
          onChange={(t) => emit(date, t)}
          label={label ? `${label} — ${t('common.time')}` : t('common.time')}
          placeholder={timePlaceholder}
          disabled={disabled || !date}
          minTime={minTime}
          maxTime={maxTime}
          // A dátum-fél már ad „×"-t az egész érték törlésére; az időnek külön törlése félreérthető lenne
          // (dátum idő nélkül → a `emit` 00:00-ra esne vissza, ami néma adat-változás).
          clearable={false}
        />
      </div>
    </div>
  );
}
